import type { Media } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from '../ui/ImgWithSkeleton';

const USAGE_INDICATOR_LABELS: Record<string, { label: string; tooltip: string }> = {
  logo: { label: 'Logo', tooltip: 'Currently used as the venue logo' },
  hero: { label: 'Hero', tooltip: 'Currently used as a hero image on the venue page' },
  gallery: { label: 'Gallery', tooltip: 'Currently shown in the venue photo gallery' },
};

const getUsageIndicators = (media: Media) =>
  (['logo', 'hero', 'gallery'] as const)
    .filter((tag) => media.tags.includes(tag))
    .map((tag) => USAGE_INDICATOR_LABELS[tag]);

const CheckIcon = () => (
  <svg className="w-3 h-3 text-white m-0.5" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export interface MediaPickerTileProps {
  media: Media;
  mode: 'single' | 'multi' | 'multi-ordered';
  isSelected: boolean;
  isAtSelectionLimit: boolean;
  selectionIndex: number;
  onSelect: (media: Media) => void;
}

export const MediaPickerTile = ({
  media,
  mode,
  isSelected,
  isAtSelectionLimit,
  selectionIndex,
  onSelect,
}: MediaPickerTileProps) => {
  const usageIndicators = getUsageIndicators(media);

  return (
    <div
      onClick={() => onSelect(media)}
      className={`relative bg-gray-50 rounded-lg shadow-sm border-2 overflow-hidden cursor-pointer transition-all ${
        isSelected
          ? 'border-primary-500 ring-2 ring-primary-200'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
      } ${
        mode !== 'single' && isAtSelectionLimit && !isSelected
          ? 'opacity-50 cursor-not-allowed'
          : ''
      }`}
    >
      {/* Selection indicator */}
      <div className="absolute top-2 left-2 z-10">
        {mode === 'single' ? (
          <div
            className={`w-5 h-5 rounded-full border-2 ${
              isSelected ? 'bg-primary-600 border-primary-600' : 'bg-gray-50 border-gray-300'
            }`}
          >
            {isSelected && <CheckIcon />}
          </div>
        ) : (
          <div
            className={`w-5 h-5 rounded border-2 ${
              isSelected ? 'bg-primary-600 border-primary-600' : 'bg-gray-50 border-gray-300'
            }`}
          >
            {isSelected && <CheckIcon />}
          </div>
        )}
      </div>

      {/* Order indicator for multi-ordered mode */}
      {mode === 'multi-ordered' && isSelected && selectionIndex >= 0 && (
        <div className="absolute top-2 right-2 z-10">
          <div className="w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {selectionIndex + 1}
          </div>
        </div>
      )}

      {/* Image */}
      <div className="aspect-square bg-gray-100">
        <ImgWithSkeleton
          src={media.cloudinaryUrl}
          alt={media.filename}
          className="w-full h-full object-cover"
          skeletonClassName="rounded-t-lg"
        />
      </div>

      {/* Media info */}
      <div className="p-2 font-sans">
        {usageIndicators.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {usageIndicators.map((indicator) => (
              <span
                key={indicator.label}
                title={indicator.tooltip}
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-50 text-primary-800 border border-primary-200"
              >
                {indicator.label}
              </span>
            ))}
          </div>
        )}
        <p className="text-xs font-medium text-gray-900 truncate">{media.filename}</p>
        {media.caption && (
          <p className="text-xs text-gray-600 truncate mt-0.5">{media.caption}</p>
        )}
      </div>
    </div>
  );
};
